import { TILE_HEIGHT, TILE_WIDTH, furnitureDef } from '@kopi-kaki/protocol';
import { PixelCanvas } from './pixel.js';
import { OUTLINE_COLOR } from './palette.js';
import { CORE_DRAWERS } from './art/core.js';
import { BAR_DRAWERS } from './art/bar.js';
import { GAMES_DRAWERS } from './art/games.js';
import { HH, HW, box, drawBox, faces, iso } from './art/iso.js';
import type { FurniDrawer } from './art/iso.js';
import { SINGAPORE_DRAWERS } from './art/singapore.js';
import { WORK_DRAWERS } from './art/work.js';
import { ARCADE_DRAWERS } from './art/arcade.js';
import { PET_DRAWERS } from './art/pets.js';
import { RARE_DRAWERS } from './art/rare.js';
import { PRIZE_DRAWERS } from './art/prizes.js';
import { HOME_DRAWERS } from './art/home.js';
import { MEDIA_DRAWERS } from './art/media.js';
import { STADIUM_DRAWERS } from './art/stadium.js';
import { POOL_DRAWERS } from './art/pool.js';
import { FESTIVE_DRAWERS } from './art/festive.js';
import { SPORTS_DRAWERS } from './art/sports.js';
import { STAFF_DRAWERS } from './art/staff.js';

/**
 * Procedural pixel-art furniture.
 *
 * Drawers live in ./art, one file per feature (core, singapore, games, work),
 * all built from the isometric helpers in ./art/iso.ts. Each item is painted
 * back to front, then gets the same dark outline as the avatars.
 */

export { iso } from './art/iso.js';
export type { FurniDrawer } from './art/iso.js';

export const FURNI_WIDTH = 96;
export const FURNI_HEIGHT = 144;
/** Canvas position of the tile centre at floor level; the sprite anchor. */
export const FURNI_ORIGIN = { x: 48, y: 124 } as const;

/** Merges drawer sections; a kind defined twice is a bug, so it throws. */
export function mergeDrawers(sections: ReadonlyArray<Record<string, FurniDrawer>>): Record<string, FurniDrawer> {
  const merged: Record<string, FurniDrawer> = {};
  for (const section of sections) {
    for (const [kind, drawer] of Object.entries(section)) {
      if (merged[kind]) throw new Error(`Duplicate furniture drawer: ${kind}`);
      merged[kind] = drawer;
    }
  }
  return merged;
}

const DRAWERS = mergeDrawers([
  CORE_DRAWERS,
  BAR_DRAWERS,
  SINGAPORE_DRAWERS,
  GAMES_DRAWERS,
  WORK_DRAWERS,
  ARCADE_DRAWERS,
  PET_DRAWERS,
  RARE_DRAWERS,
  HOME_DRAWERS,
  PRIZE_DRAWERS,
  MEDIA_DRAWERS,
  STADIUM_DRAWERS,
  POOL_DRAWERS,
  FESTIVE_DRAWERS,
  SPORTS_DRAWERS,
  STAFF_DRAWERS,
]);

export function hasDrawer(kind: string): boolean {
  return Object.prototype.hasOwnProperty.call(DRAWERS, kind);
}

/** Placeholder crate with a question mark, for kinds with no art yet. */
export function drawUnknown(pc: PixelCanvas): void {
  drawBox(pc, box(-0.3, 0.3, -0.3, 0.3, 0, 24, faces('#b9a58a')));
  const q = iso(0.3, 0, 12);
  pc.rect(q.x - 2, q.y - 6, 4, 2, '#3b2a20');
  pc.rect(q.x + 1, q.y - 4, 2, 2, '#3b2a20');
  pc.rect(q.x - 1, q.y - 2, 2, 2, '#3b2a20');
  pc.rect(q.x - 1, q.y + 2, 2, 2, '#3b2a20');
}

export function drawFurni(kind: string, rotation: number, open?: boolean): PixelCanvas {
  const pc = new PixelCanvas(FURNI_WIDTH, FURNI_HEIGHT);
  pc.ox = FURNI_ORIGIN.x;
  pc.oy = FURNI_ORIGIN.y;
  const drawer = hasDrawer(kind) ? DRAWERS[kind]! : drawUnknown;
  // Anything with some height throws a soft shadow onto the floor beneath it.
  if ((furnitureDef(kind)?.height ?? 0.5) >= 0.2) {
    pc.ellipse(2, 3, HW * 0.92, HH * 0.9, {
      type: 'boxRadial',
      fx: 0.5,
      fy: 0.5,
      reach: Math.SQRT1_2,
      stops: [[0, '#1a12103a'], [0.6, '#1a121026'], [1, '#1a121000']],
    });
  }
  drawer(pc, rotation, open);
  pc.ox = 0;
  pc.oy = 0;
  pc.outlineSelective(OUTLINE_COLOR);
  // A drawer that never reads `rotation` looks the same from every side, so
  // mirror it for the far-facing turns and let every item visibly rotate.
  if (drawer.length < 2 && (rotation === 4 || rotation === 6)) {
    const mirrored = new PixelCanvas(FURNI_WIDTH, FURNI_HEIGHT);
    mirrored.blit(pc.flipX(), 1, 0); // flipping maps the anchor column 48 to 47
    return mirrored;
  }
  return pc;
}

/* -------------------------------------------------------------------------- */
/* Small sprites                                                              */
/* -------------------------------------------------------------------------- */

/** A soft elliptical shadow, densest in the middle; translucent, so it is never outlined. */
export function drawShadow(width: number, height: number): PixelCanvas {
  const pc = new PixelCanvas(width, height);
  pc.ellipse(width / 2, height / 2, width / 2, height / 2, {
    type: 'boxRadial',
    fx: 0.5,
    fy: 0.5,
    reach: Math.SQRT1_2,
    stops: [[0, '#1a12104d'], [0.55, '#1a121038'], [1, '#1a121000']],
  });
  return pc;
}

/**
 * Destination marker: a white double outline with a faint dark edge (so it
 * still reads on pale floors) and no fill, so the floor stays visible.
 */
export function drawTileTarget(): PixelCanvas {
  const pc = new PixelCanvas(TILE_WIDTH + 2, TILE_HEIGHT + 2);
  pc.ox = HW + 1;
  pc.oy = 1;
  const ring = (inset: number, color: string) => {
    const w = HW - inset * 2;
    const top = inset;
    const bottom = TILE_HEIGHT - inset;
    pc.line(0, top, w, HH, color);
    pc.line(w, HH, 0, bottom, color);
    pc.line(0, bottom, -w, HH, color);
    pc.line(-w, HH, 0, top, color);
  };
  ring(0, '#2a1e1960');
  ring(1, '#ffffff');
  ring(2, '#ffffff');
  return pc;
}

/** Tile cursor: a bright diamond outline with a faint fill. */
export function drawTileCursor(color = '#ffffff'): PixelCanvas {
  const pc = new PixelCanvas(TILE_WIDTH + 2, TILE_HEIGHT + 2);
  pc.ox = HW + 1;
  pc.oy = 1;
  pc.poly([{ x: 0, y: 0 }, { x: HW, y: HH }, { x: 0, y: TILE_HEIGHT }, { x: -HW, y: HH }], `${color}30`);
  pc.line(0, 0, HW, HH, color);
  pc.line(HW, HH, 0, TILE_HEIGHT, color);
  pc.line(0, TILE_HEIGHT, -HW, HH, color);
  pc.line(-HW, HH, 0, 0, color);
  return pc;
}
